/**
 * CountryService - Service pour récupérer les informations des pays
 * Utilise l'API RestCountries
 * @class CountryService
 */
import { ApiService } from './ApiService.js';
import { Country } from '../models/Country.js';

export class CountryService extends ApiService {
    /**
     * Crée une instance de CountryService
     */
    constructor() {
        super('https://restcountries.com/v3.1');
        this._cache = new Map();
        this._allCountriesCache = null;
    }

    /**
     * Récupère tous les pays
     * @returns {Promise<Country[]>} Liste des pays
     */
    async getAllCountries() {
        if (this._allCountriesCache) {
            return this._allCountriesCache;
        }

        try {
            const data = await this.get('all', {
                fields: 'name,cca2,capital,region,population,flags,currencies,latlng,borders,translations'
            });

            this._allCountriesCache = data
                .map(countryData => Country.fromApiData(countryData))
                .sort((a, b) => a.name.localeCompare(b.name, 'fr'));

            // Met en cache chaque pays par code
            this._allCountriesCache.forEach(country => {
                this._cache.set(country.code, country);
            });

            return this._allCountriesCache;
        } catch (error) {
            console.error('Error fetching all countries:', error);
            throw error;
        }
    }

    /**
     * Récupère un pays par son code ISO
     * @param {string} code - Code ISO du pays (ex: FR, US)
     * @returns {Promise<Country>} Pays
     */
    async getCountryByCode(code) {
        const upperCode = code.toUpperCase();

        // Vérifie le cache
        if (this._cache.has(upperCode)) {
            return this._cache.get(upperCode);
        }

        try {
            const data = await this.get(`alpha/${upperCode}`);
            const countryData = Array.isArray(data) ? data[0] : data;
            const country = Country.fromApiData(countryData);

            // Met en cache
            this._cache.set(upperCode, country);

            return country;
        } catch (error) {
            console.error(`Error fetching country ${code}:`, error);
            throw error;
        }
    }

    /**
     * Recherche des pays par nom
     * @param {string} name - Nom à rechercher
     * @returns {Promise<Country[]>} Pays correspondants
     */
    async searchByName(name) {
        if (!name || name.length < 2) {
            return [];
        }

        try {
            const data = await this.get(`name/${encodeURIComponent(name)}`);
            return data.map(countryData => Country.fromApiData(countryData));
        } catch (error) {
            if (error.status === 404) {
                return [];
            }
            throw error;
        }
    }

    /**
     * Récupère les pays d'une région
     * @param {string} region - Nom de la région
     * @returns {Promise<Country[]>} Pays de la région
     */
    async getByRegion(region) {
        try {
            const data = await this.get(`region/${encodeURIComponent(region)}`);
            return data.map(countryData => Country.fromApiData(countryData));
        } catch (error) {
            console.error(`Error fetching region ${region}:`, error);
            throw error;
        }
    }

    /**
     * Récupère les pays utilisant une devise spécifique
     * @param {string} currencyCode - Code de la devise
     * @returns {Promise<Country[]>} Pays utilisant cette devise
     */
    async getByCurrency(currencyCode) {
        try {
            const data = await this.get(`currency/${currencyCode.toLowerCase()}`);
            return data.map(countryData => Country.fromApiData(countryData));
        } catch (error) {
            if (error.status === 404) {
                return [];
            }
            throw error;
        }
    }

    /**
     * Récupère les pays frontaliers
     * @param {Country} country - Pays dont on veut les voisins
     * @returns {Promise<Country[]>} Pays frontaliers
     */
    async getBorderCountries(country) {
        if (!country.borders || country.borders.length === 0) {
            return [];
        }

        try {
            const codes = country.borders.join(',');
            const data = await this.get('alpha', { codes });
            return data.map(countryData => Country.fromApiData(countryData));
        } catch (error) {
            console.error('Error fetching border countries:', error);
            return [];
        }
    }

    /**
     * Vide le cache
     */
    clearCache() {
        this._cache.clear();
        this._allCountriesCache = null;
    }
}
